import React, { forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { fetchProductVariations } from '../../services/productService';

/**
 * ProductOptionsSelector
 *
 * Interactive option selection component. Renders each variation group as:
 *   - Color swatches (32 px circles) when any value has hex_color
 *   - Toggle chip buttons when no hex_color and ≤ 5 values
 *   - MUI Select dropdown when no hex_color and > 5 values
 *
 * Exposes a `validate()` method via ref that sets error state and returns a
 * boolean. Also calls onSelectionChange(selectedOptions, isValid) on every
 * change, where selectedOptions is { [optionName]: { value_id, title, price_modifier } }.
 */
const ProductOptionsSelector = forwardRef(function ProductOptionsSelector(
  { product, onSelectionChange },
  ref
) {
  const { data: variations } = useQuery({
    queryKey: ['product-variations', product?.id],
    queryFn: () => fetchProductVariations(product.id),
    enabled: Boolean(product?.id),
    staleTime: 5 * 60 * 1000,
  });

  // { [optionId]: { value_id, title, price_modifier } }
  const [selected, setSelected] = useState({});
  const [errors, setErrors] = useState({});

  const computeIsValid = useCallback(
    (currentSelected, vars) => {
      if (!vars || vars.length === 0) return true;
      return vars
        .filter((v) => v.is_required)
        .every((v) => Boolean(currentSelected[v.id]));
    },
    []
  );

  // Expose validate() so ProductDetail can call ref.current.validate() before add-to-cart
  useImperativeHandle(ref, () => ({
    validate() {
      if (!variations || variations.length === 0) return true;
      const newErrors = {};
      for (const v of variations) {
        if (v.is_required && !selected[v.id]) {
          newErrors[v.id] = true;
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
  }));

  if (!variations || variations.length === 0) return null;

  const formatPriceMod = (mod) => {
    if (!mod || mod === 0) return '';
    const sign = mod > 0 ? '+' : '-';
    return ` (${sign}$${Math.abs(mod).toFixed(2)})`;
  };

  const handleSelect = (optionId, optionName, valueObj) => {
    const next = {
      ...selected,
      [optionId]: {
        value_id: valueObj.id,
        title: valueObj.title,
        price_modifier: valueObj.price_modifier ?? 0,
      },
    };
    setSelected(next);

    // Clear error for this group on selection
    if (errors[optionId]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[optionId];
        return e;
      });
    }

    if (onSelectionChange) {
      // Build keyed-by-name map for external consumers
      const byName = {};
      variations.forEach((v) => {
        if (next[v.id]) {
          byName[v.name] = next[v.id];
        }
      });
      onSelectionChange(byName, computeIsValid(next, variations));
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      {variations.map((option) => {
        const { id: optionId, name, custom_title, is_required, values = [] } = option;
        const label = custom_title || name;
        const currentSelection = selected[optionId];
        const hasError = Boolean(errors[optionId]);
        const hasColors = values.some((v) => Boolean(v.hex_color));

        return (
          <Box key={optionId} sx={{ mb: 3 }}>
            {/* Option label */}
            <Typography variant="subtitle2" gutterBottom>
              {label}
              {is_required && (
                <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>

            {/* Color swatch selector */}
            {hasColors && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  {values.map((val) => {
                    const isSelected = currentSelection?.value_id === val.id;
                    const tooltipLabel = `${val.title}${formatPriceMod(val.price_modifier)}`;
                    return (
                      <Tooltip key={val.id} title={tooltipLabel} arrow>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleSelect(optionId, name, val)}
                          aria-label={tooltipLabel}
                          aria-pressed={isSelected}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: val.hex_color || 'grey.300',
                            border: isSelected ? '3px solid' : '2px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            boxShadow: isSelected ? 3 : 0,
                            cursor: 'pointer',
                            padding: 0,
                            outline: 'none',
                            transition: 'box-shadow 0.15s, border-color 0.15s',
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: 'primary.light',
                            },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: '2px',
                            },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                {hasError && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    Please select an option
                  </FormHelperText>
                )}
              </>
            )}

            {/* Toggle chip selector (≤5 non-color values) */}
            {!hasColors && values.length <= 5 && (
              <>
                <ToggleButtonGroup
                  value={currentSelection?.value_id ?? null}
                  exclusive
                  onChange={(_, newVal) => {
                    if (newVal === null) return;
                    const val = values.find((v) => v.id === newVal);
                    if (val) handleSelect(optionId, name, val);
                  }}
                  sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                >
                  {values.map((val) => (
                    <ToggleButton
                      key={val.id}
                      value={val.id}
                      size="small"
                      sx={{
                        borderRadius: '16px !important',
                        px: 1.5,
                        textTransform: 'none',
                        fontSize: '0.8125rem',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderColor: 'primary.main',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      {val.title}
                      {val.price_modifier !== 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 0.5, opacity: 0.85 }}
                        >
                          {formatPriceMod(val.price_modifier)}
                        </Typography>
                      )}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {hasError && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    Please select an option
                  </FormHelperText>
                )}
              </>
            )}

            {/* Dropdown selector (>5 non-color values) */}
            {!hasColors && values.length > 5 && (
              <FormControl fullWidth size="small" error={hasError} sx={{ mt: 0.5 }}>
                <Select
                  displayEmpty
                  value={currentSelection?.value_id ?? ''}
                  onChange={(e) => {
                    const val = values.find((v) => v.id === e.target.value);
                    if (val) handleSelect(optionId, name, val);
                  }}
                  renderValue={(v) => {
                    if (!v) {
                      return (
                        <Typography color="text.secondary" variant="body2">
                          Select {label}
                        </Typography>
                      );
                    }
                    const val = values.find((vv) => vv.id === v);
                    return val
                      ? `${val.title}${formatPriceMod(val.price_modifier)}`
                      : v;
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography color="text.secondary">Select {label}</Typography>
                  </MenuItem>
                  {values.map((val) => (
                    <MenuItem key={val.id} value={val.id}>
                      {val.title}
                      {val.price_modifier !== 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          {formatPriceMod(val.price_modifier)}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {hasError && (
                  <FormHelperText>Please select an option</FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

export default ProductOptionsSelector;
